import { useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import { ErrorIcon } from '@icons';
import { useLegFormContext } from '@lp/features/RoutePlanner/components/RouteLegForm/context/LegFormContext';
import {
  BaseAccommodationLegSchemaValues,
  BaseGroundLegSchemaValues,
  CommonRouteLegSchemaValues,
  LegFormValues,
  validationLegWarning,
} from '@lp/features/RoutePlanner/components/RouteLegForm/validation';
import { useLegFormAircraftUtilizationWarnings } from '@lp/features/RoutePlanner/components/RouteLegForm/hooks';
import { getErrorMessages } from '@/utils/helpers';
import { useWatchFields } from '@/hooks/useWatchFields';
import { WarningsListBanner } from './WarningsListBanner';

type ValidationBannerFormFields = CommonRouteLegSchemaValues &
  BaseAccommodationLegSchemaValues &
  Pick<BaseGroundLegSchemaValues, 'isFeasibleForDrive'>;

export const ValidationBanners = () => {
  const { formState } = useFormContext<LegFormValues>();
  const { errorMessages, warningMessages } = useLegFormContext();
  const {
    segmentType,
    minDate,
    maxDate,
    departureDateTime,
    arrivalDateTime,
    legType,
    checkInAt,
    checkOutAt,
    timeDurationInMs,
    isFeasibleForDrive,
  } = useWatchFields<ValidationBannerFormFields>([
    'legType',
    'segmentType',
    'minDate',
    'maxDate',
    'arrivalDateTime',
    'departureDateTime',
    'legType',
    'checkInAt',
    'checkOutAt',
    'timeDurationInMs',
    'isFeasibleForDrive',
  ] as const);
  const aircraftUtilizationWarnings = useLegFormAircraftUtilizationWarnings();

  const warnings = useMemo(() => {
    // TODO: refactor
    const validatedWarnings = validationLegWarning({
      segmentType,
      minDate,
      maxDate,
      arrivalDateTime: arrivalDateTime || checkOutAt,
      departureDateTime: departureDateTime || checkInAt,
      legType,
      timeDurationInMs,
      isFeasibleForDrive,
    });

    return [...(warningMessages || []), ...validatedWarnings, ...aircraftUtilizationWarnings];
  }, [
    warningMessages,
    aircraftUtilizationWarnings,
    segmentType,
    minDate,
    maxDate,
    departureDateTime,
    arrivalDateTime,
    legType,
    checkInAt,
    checkOutAt,
    timeDurationInMs,
    isFeasibleForDrive,
  ]);

  const errorsList = useMemo(() => {
    const errors = getErrorMessages(formState.errors);
    return [...errors, ...(errorMessages || [])];
  }, [formState, errorMessages]);

  if (!warnings?.length && !errorsList.length) return null;

  return (
    <>
      <WarningsListBanner messages={warnings} />
      {!!errorsList.length && (
        <div className="w-full flex px-2 items-stretch border-l-8 border-danger-40 bg-danger-70 overflow-hidden rounded-lg">
          <div className="flex flex-col gap-1 py-2 pr-2">
            {errorsList.map((error) => (
              <div key={error} className="flex gap-2 text-danger-40 items-center">
                <div className="shrink-0">
                  <ErrorIcon />
                </div>
                <span className="text-m-r text-danger-5">{error}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};
